import { DEFAULT_HIDDEN_SECTIONS } from '../config/reportTemplate';
import type { WorkshopState } from './types';

/**
 * The shape the frontend assumes before the first server response arrives, and
 * what a brand-new workshop looks like. The backend owns the real default; this
 * exists so components never have to null-check every field.
 */
export function emptyWorkshopState(
  workshopId: string,
  /**
   * Section ids the campaign's admin flagged hidden-by-default (from the DB report-section config,
   * mapped through `RunnerReportSection.hiddenByDefault`). Unioned with the runner's bundled
   * `DEFAULT_HIDDEN_SECTIONS` to form the report's starting hidden set, so a section marked
   * hidden-by-default is left out of both the editor and the export until the SE un-hides it. Empty
   * for the standalone demo / un-templated campaigns, where only the bundled defaults apply.
   */
  hiddenByDefault: string[] = [],
): WorkshopState {
  return {
    workshopId,
    current: 0,
    videoWatched: [false, false, false, false, false, false],
    checklists: {},
    delivered: [false, false, false, false, false, false],
    visited: [false, false, false, false, false, false],
    slide: 0,
    connect: {
      ready: { connected: false, capture: null },
      iq: { connected: false, capture: null },
    },
    installBase: null,
    comments: {},
    discoveryAnswers: {},
    gapStatus: {},
    gapRowOverrides: {},
    reportEdits: {},
    reportLayout: {
      hidden: unionHidden(hiddenByDefault),
      order: [],
      custom: [],
      titles: {},
      charts: [],
      images: [],
      chartsHidden: [],
      chartOrder: [],
      chartNotes: {},
      chartSections: {},
      figureAnchors: {},
      aiOverride: {},
      preloaded: {},
    },
    reportImages: {},
    deckEdits: {},
    aiQuestions: {},
    aiDrafts: {},
    guideResponses: {},
    notesSummary: '',
  };
}

/**
 * The report's starting hidden set: the runner's bundled defaults plus whatever the campaign admin
 * flagged hidden-by-default, de-duplicated (a bundled id can also be flagged in the DB). Order is
 * preserved with the bundled ids first so the list stays stable/readable.
 */
function unionHidden(hiddenByDefault: string[]): string[] {
  return Array.from(new Set([...DEFAULT_HIDDEN_SECTIONS, ...hiddenByDefault]));
}

const STEP_COUNT = 6;

/**
 * Fills in anything the backend omitted. A partial response (an older record, a
 * field added after this workshop's row was written) should not crash a render.
 * The per-step arrays are also resized, so a record written when the flow had
 * fewer steps still loads.
 */
export function normalizeWorkshopState(
  raw: Partial<WorkshopState>,
  workshopId: string,
  /** See {@link emptyWorkshopState}. Only consulted at first-seed (a record with no stored `hidden`). */
  hiddenByDefault: string[] = [],
): WorkshopState {
  const base = emptyWorkshopState(workshopId, hiddenByDefault);
  const flags = (value: unknown): boolean[] => {
    const source = Array.isArray(value) ? value : [];
    return Array.from({ length: STEP_COUNT }, (_, i) => !!source[i]);
  };

  return {
    ...base,
    ...raw,
    workshopId: raw.workshopId ?? workshopId,
    current:
      typeof raw.current === 'number' ? Math.min(STEP_COUNT - 1, Math.max(0, raw.current)) : 0,
    videoWatched: flags(raw.videoWatched),
    delivered: flags(raw.delivered),
    visited: flags(raw.visited),
    slide: typeof raw.slide === 'number' ? raw.slide : 0,
    checklists: raw.checklists ?? {},
    installBase: raw.installBase ?? null,
    comments: raw.comments ?? {},
    discoveryAnswers: raw.discoveryAnswers ?? {},
    gapStatus: raw.gapStatus ?? {},
    gapRowOverrides: raw.gapRowOverrides ?? {},
    reportEdits: raw.reportEdits ?? {},
    reportLayout: {
      // A record written before this field existed has no opinion about the
      // standing sections, so it gets the default (bundled defaults unioned with
      // the campaign's admin-flagged hidden-by-default sections; see `base`). An
      // SE who has genuinely unhidden everything stored an empty array, which is
      // not undefined, so this only ever fires at first-seed.
      hidden: raw.reportLayout?.hidden ?? base.reportLayout.hidden,
      order: raw.reportLayout?.order ?? [],
      custom: raw.reportLayout?.custom ?? [],
      titles: raw.reportLayout?.titles ?? {},
      charts: raw.reportLayout?.charts ?? [],
      images: raw.reportLayout?.images ?? [],
      chartsHidden: raw.reportLayout?.chartsHidden ?? [],
      chartOrder: raw.reportLayout?.chartOrder ?? [],
      chartNotes: raw.reportLayout?.chartNotes ?? {},
      chartSections: raw.reportLayout?.chartSections ?? {},
      figureAnchors: raw.reportLayout?.figureAnchors ?? {},
      aiOverride: raw.reportLayout?.aiOverride ?? {},
      preloaded: raw.reportLayout?.preloaded ?? {},
    },
    reportImages: raw.reportImages ?? {},
    deckEdits: raw.deckEdits ?? {},
    aiQuestions: raw.aiQuestions ?? {},
    aiDrafts: raw.aiDrafts ?? {},
    guideResponses: raw.guideResponses ?? {},
    notesSummary: raw.notesSummary ?? '',
    connect: {
      ready: raw.connect?.ready ?? base.connect.ready,
      iq: raw.connect?.iq ?? base.connect.iq,
    },
  };
}
